import { BrandPlan, BrandPlanTactic, BrandPlanIntegrations, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { getTacticsFromContentCounts } from 'screens/BrandKits/components/BrandPlan/utils'
import { getTacticInfo } from 'components/TacticIntegrationList/utils'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'

/**
 * Combines tactics from brand plan and content counts, normalizing and deduplicating them.
 * InstagramReel is normalized to Instagram since they map to the same integration.
 */
export const getNormalizedTactics = (brandPlan: BrandPlan | null | undefined): BrandPlanIntegrations[] => {
  if (!brandPlan) {
    return []
  }

  const allTactics = [...brandPlan.tactics, ...getTacticsFromContentCounts(brandPlan)]

  const normalizedTactics = allTactics.map((tactic) => normalizeTactic(tactic))

  return Array.from(new Set(normalizedTactics))
}

export const normalizeTactic = (tactic: BrandPlanIntegrations): BrandPlanIntegrations => {
  return tactic === BrandPlanTactic.InstagramReel ? BrandPlanTactic.Instagram : tactic
}

/**
 * Counts only integrations that match the selected tactics and platforms.
 * For email/blog integrations, only counts if they match the selected platform.
 */
export const getConnectedAccountsCount = (
  externalIntegrations: ExternalIntegration[] | undefined,
  tactics: BrandPlanIntegrations[],
  selectedBlogPlatform: SelectedBlogPlatform | undefined,
  selectedEmailPlatform: SelectedEmailPlatform | undefined,
): number => {
  if (!externalIntegrations) {
    return 0
  }

  // Get all tactics including 'google' which is always shown
  const allTacticsForCounting: BrandPlanIntegrations[] = [...tactics, 'google']

  // Get integration types for each tactic (filtering out nulls for SEO/Email without platforms)
  const integrationTypes = allTacticsForCounting
    .map((tactic) => getTacticInfo(tactic, selectedBlogPlatform, selectedEmailPlatform))
    .filter((info): info is NonNullable<typeof info> => info !== null)
    .map((info) => info.integrationType)

  // Count integrations that match our tactics' integration types
  return externalIntegrations.filter((integration: ExternalIntegration) =>
    integrationTypes.includes(integration.provider),
  ).length
}
